/**
 * DOCUMENT UPLOAD PAGE
 * Supports: images (JPG/PNG/WEBP), PDFs, TXT, JSON, CSV
 * Two modes:
 *   1. Create new case from document (POST /api/upload/document)
 *   2. Attach document to existing case (POST /api/upload/document-to-case/:caseId)
 * Features: drag-and-drop, batch upload, progress tracking, LLM OCR for images
 */
import { useState, useRef, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileImage,
  FileText,
  FileJson,
  FileSpreadsheet,
  File,
  CheckCircle2,
  XCircle,
  Loader2,
  X,
  Eye,
  ExternalLink,
  ImageIcon,
  Paperclip,
  Plus,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface UploadFile {
  id: string;
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  result?: {
    instanceId?: string;
    evidenceId?: string;
    caseUrl?: string;
    storageUrl?: string;
    extractedLength?: number;
    extractedPreview?: string;
    filename?: string;
  };
  error?: string;
  preview?: string; // data URL for images
}

interface UploadResult {
  success: boolean;
  instanceId?: string;
  evidenceId?: string;
  recordId?: string;
  caseUrl?: string;
  filename?: string;
  fileSize?: number;
  storageUrl?: string;
  extractedLength?: number;
  extractedPreview?: string;
  importSummary?: { format: string; imported: number; failed: number };
  error?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getFileIcon(file: File) {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext))
    return <FileImage className="h-5 w-5 text-blue-400" />;
  if (ext === "pdf") return <File className="h-5 w-5 text-red-400" />;
  if (ext === "json") return <FileJson className="h-5 w-5 text-yellow-400" />;
  if (ext === "csv") return <FileSpreadsheet className="h-5 w-5 text-green-400" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DocumentUpload() {
  const [, navigate] = useLocation();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState<"new-case" | "attach-case">("new-case");
  const [caseId, setCaseId] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── File selection ──────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    const newFiles: UploadFile[] = arr.map((file) => {
      const uf: UploadFile = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: "pending",
      };
      // Generate preview for images
      if (isImageFile(file)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uf.id ? { ...f, preview: e.target?.result as string } : f
            )
          );
        };
        reader.readAsDataURL(file);
      }
      return uf;
    });
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // ── Drag and drop ───────────────────────────────────────────────────────────

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = "";
    }
  };

  // ── Upload logic ────────────────────────────────────────────────────────────

  const uploadSingleFile = async (uf: UploadFile): Promise<UploadResult> => {
    const formData = new FormData();
    formData.append("file", uf.file);

    const endpoint =
      mode === "attach-case" && caseId.trim()
        ? `/api/upload/document-to-case/${caseId.trim()}`
        : `/api/upload/document`;

    const response = await fetch(endpoint, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    const data: UploadResult = await response.json();
    if (!response.ok && !data.error) {
      data.error = `HTTP ${response.status}`;
    }
    return data;
  };

  const handleUpload = async () => {
    const pending = files.filter((f) => f.status === "pending");
    if (pending.length === 0) {
      toast.error("No files to upload", {
        description: "Add files using the drop zone or file picker",
      });
      return;
    }

    if (mode === "attach-case" && !caseId.trim()) {
      toast.error("Case ID required", {
        description: "Enter the case ID to attach documents to",
      });
      return;
    }

    setIsUploading(true);

    // Mark all pending as uploading
    setFiles((prev) =>
      prev.map((f) => (f.status === "pending" ? { ...f, status: "uploading" } : f))
    );

    let succeeded = 0;
    let failed = 0;

    for (const uf of pending) {
      try {
        const result = await uploadSingleFile(uf);
        if (result.success) {
          succeeded++;
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uf.id
                ? { ...f, status: "success", result }
                : f
            )
          );
        } else {
          failed++;
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uf.id
                ? { ...f, status: "error", error: result.error ?? "Upload failed" }
                : f
            )
          );
        }
      } catch (err) {
        failed++;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uf.id
              ? { ...f, status: "error", error: String(err) }
              : f
          )
        );
      }
    }

    setIsUploading(false);

    if (succeeded > 0 && failed === 0) {
      toast.success(`${succeeded} document${succeeded > 1 ? "s" : ""} uploaded`, {
        description:
          mode === "new-case"
            ? "New cases created from your documents"
            : `Documents attached to case ${caseId}`,
      });
    } else if (succeeded > 0) {
      toast.warning(`${succeeded} succeeded, ${failed} failed`);
    } else {
      toast.error("All uploads failed", {
        description: "Check the error messages below",
      });
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const successCount = files.filter((f) => f.status === "success").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border/40 bg-card/50">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Link href="/control-room">
              <Button variant="ghost" size="icon" className="shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Upload className="h-6 w-6 text-primary" />
                Upload Documents
              </h1>
              <p className="text-sm text-muted-foreground">
                Upload images, PDFs, and documents — text is extracted automatically
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8 space-y-6 max-w-4xl">
        {/* Mode Selection */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setMode("new-case")}
            className={`p-4 rounded-lg border text-left transition-all ${
              mode === "new-case"
                ? "border-primary bg-primary/10 ring-1 ring-primary"
                : "border-border/40 bg-card hover:border-border"
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Plus className={`h-5 w-5 ${mode === "new-case" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="font-semibold">Create New Cases</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Each document becomes a new case in the system
            </p>
          </button>
          <button
            onClick={() => setMode("attach-case")}
            className={`p-4 rounded-lg border text-left transition-all ${
              mode === "attach-case"
                ? "border-primary bg-primary/10 ring-1 ring-primary"
                : "border-border/40 bg-card hover:border-border"
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Paperclip className={`h-5 w-5 ${mode === "attach-case" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="font-semibold">Attach to Existing Case</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Add documents as evidence to a case you already have
            </p>
          </button>
        </div>

        {/* Case ID input (attach mode) */}
        {mode === "attach-case" && (
          <Card className="border-border/40 bg-card/50">
            <CardContent className="pt-6">
              <label className="block text-sm font-medium mb-2">Case ID</label>
              <input
                type="text"
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                className="w-full px-3 py-2 rounded-md border border-border/60 bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Find the case ID in the URL when viewing a case: /instance?id=<span className="font-mono">CASE-ID</span>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative rounded-xl border-2 border-dashed transition-all cursor-pointer ${
            isDragging
              ? "border-primary bg-primary/10 scale-[1.01]"
              : "border-border/40 bg-card/30 hover:border-primary/50 hover:bg-card/50"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.json,.csv"
            onChange={handleFileInput}
            className="hidden"
          />
          <div className="py-12 text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                <FileText className="h-6 w-6 text-muted-foreground/40 absolute -bottom-1 -right-2" />
              </div>
            </div>
            <p className="text-lg font-medium text-foreground/80">
              {isDragging ? "Drop files here" : "Drag & drop files here"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              or click to browse
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {["JPG", "PNG", "PDF", "TXT", "JSON", "CSV"].map((ext) => (
                <Badge key={ext} variant="outline" className="text-xs">
                  {ext}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Images are processed with AI text extraction (OCR) · Max 16MB per file
            </p>
          </div>
        </div>

        {/* File Queue */}
        {files.length > 0 && (
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  Files ({files.length})
                  {successCount > 0 && (
                    <Badge variant="outline" className="ml-2 text-emerald-400 border-emerald-500/30">
                      {successCount} done
                    </Badge>
                  )}
                  {errorCount > 0 && (
                    <Badge variant="outline" className="ml-2 text-red-400 border-red-500/30">
                      {errorCount} failed
                    </Badge>
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiles([])}
                  className="text-muted-foreground"
                >
                  Clear all
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {files.map((uf) => (
                <div
                  key={uf.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                    uf.status === "success"
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : uf.status === "error"
                      ? "border-red-500/30 bg-red-500/5"
                      : uf.status === "uploading"
                      ? "border-primary/30 bg-primary/5"
                      : "border-border/30 bg-muted/20"
                  }`}
                >
                  {/* Preview or icon */}
                  <div className="shrink-0 w-10 h-10 rounded overflow-hidden flex items-center justify-center bg-muted/50">
                    {uf.preview ? (
                      <img src={uf.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      getFileIcon(uf.file)
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{uf.file.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatBytes(uf.file.size)}
                      </span>
                    </div>

                    {/* Status */}
                    {uf.status === "uploading" && (
                      <div className="flex items-center gap-2 mt-1">
                        <Loader2 className="h-3 w-3 animate-spin text-primary" />
                        <span className="text-xs text-primary">Uploading & extracting text…</span>
                      </div>
                    )}
                    {uf.status === "success" && uf.result && (
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                          <span className="text-xs text-emerald-400">
                            {uf.result.extractedLength
                              ? `${uf.result.extractedLength.toLocaleString()} chars extracted`
                              : "Uploaded successfully"}
                          </span>
                        </div>
                        {uf.result.caseUrl && (
                          <Link href={uf.result.caseUrl}>
                            <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-2">
                              <ExternalLink className="h-3 w-3" />
                              {mode === "new-case" ? "View new case" : "View case"}
                            </Button>
                          </Link>
                        )}
                        {uf.result.extractedPreview && (
                          <p className="text-xs text-muted-foreground italic truncate">
                            "{uf.result.extractedPreview.slice(0, 120)}…"
                          </p>
                        )}
                      </div>
                    )}
                    {uf.status === "error" && (
                      <div className="flex items-center gap-2 mt-1">
                        <XCircle className="h-3 w-3 text-red-400" />
                        <span className="text-xs text-red-400">{uf.error}</span>
                      </div>
                    )}
                  </div>

                  {/* Remove button */}
                  {uf.status !== "uploading" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={() => removeFile(uf.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Upload Button */}
        {files.length > 0 && (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setFiles(files.filter((f) => f.status !== "success"))}
              disabled={isUploading}
            >
              Remove completed
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isUploading || pendingCount === 0}
              className="gap-2 min-w-[140px]"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload {pendingCount > 0 ? `${pendingCount} ` : ""}File{pendingCount !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Info panel */}
        <Card className="border-border/40 bg-card/30">
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" />
              How document upload works
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <span className="text-foreground font-medium">Images (JPG, PNG, WEBP):</span>{" "}
                AI vision reads all text visible in the image — letters, notices, forms, screenshots. The extracted text is analyzed for problem type, jurisdiction, and system.
              </p>
              <p>
                <span className="text-foreground font-medium">PDFs:</span>{" "}
                Text layer is extracted where available. Scanned PDFs may require image upload of individual pages.
              </p>
              <p>
                <span className="text-foreground font-medium">Text / CSV / JSON:</span>{" "}
                Parsed directly. JSON and CSV can contain multiple records and will create multiple cases.
              </p>
              <p>
                <span className="text-foreground font-medium">All files:</span>{" "}
                Stored securely in cloud storage with a permanent URL. The original file and extracted text are both preserved as evidence.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
